import { createActions, createReducer } from 'reduxsauce';
import {
  BASE_INITIAL_STATE,
  getRequestHandler,
  getFailureHandler,
} from '../../../store/structures';

const { Types, Creators } = createActions(
  {
    followBusinessRequest: ['data'],
    followBusinessSuccess: ['data'],
    followBusinessFailure: ['error'],
    unfollowBusinessRequest: ['data'],
    unfollowBusinessSuccess: ['data'],
    unfollowBusinessFailure: ['error'],
    getFollowedBusinessesRequest: ['params'],
    getFollowedBusinessesSuccess: ['data'],
    getFollowedBusinessesFailure: ['error'],
  },
  { prefix: 'cb/businesses/follow/' },
);

export { Types as types, Creators as actions };

const INITIAL_STATE = {
  ...BASE_INITIAL_STATE,
  data: { results: [] },
  // business_ids we've optimistically removed. Stays populated until a GET
  // response confirms the server no longer includes the business — this
  // prevents any in-flight GET from restoring a business we just unfollowed.
  _pendingUnfollowIds: [],
};

// Safely extract results regardless of legacy array shape or current object shape
const getResults = (state) => {
  if (state.data && Array.isArray(state.data.results)) return state.data.results;
  if (Array.isArray(state.data)) return state.data;
  return [];
};

// Safely spread data without blowing up on legacy array shape
const safeDataSpread = (state) =>
  Array.isArray(state.data) ? {} : (state.data || {});

export const HANDLERS = {
  [Types.FOLLOW_BUSINESS_REQUEST]: (state, action) => ({
    ...state,
    data: {
      ...safeDataSpread(state),
      results: [
        ...getResults(state),
        {
          business_id: action.data && action.data.business_id,
          is_affiliate_or_flyer: (action.data && action.data.is_affiliate_or_flyer) || false,
          _optimistic: true,
        },
      ],
    },
  }),
  [Types.FOLLOW_BUSINESS_SUCCESS]: (state, action) => ({
    ...state,
    data: {
      ...safeDataSpread(state),
      results: [
        ...getResults(state).filter(
          b => b.business_id !== (action.data && action.data.business_id)
        ),
        action.data,
      ],
    },
    fetching: false,
    fetched: true,
  }),
  [Types.FOLLOW_BUSINESS_FAILURE]: (state, action) => ({
    ...state,
    error: action.error,
    data: {
      ...safeDataSpread(state),
      results: getResults(state).filter(b => !b._optimistic),
    },
  }),
  [Types.UNFOLLOW_BUSINESS_REQUEST]: (state, action) => {
    const businessId = (action.data && action.data.business_id) != null
      ? String(action.data.business_id)
      : null;
    return {
      ...state,
      _pendingUnfollowIds: businessId
        ? [...(state._pendingUnfollowIds || []), businessId]
        : (state._pendingUnfollowIds || []),
      data: {
        ...safeDataSpread(state),
        results: getResults(state).filter(
          b => String(b.business_id) !== String(action.data && action.data.business_id) &&
               b.id !== (action.data && action.data.id)
        ),
      },
    };
  },
  [Types.UNFOLLOW_BUSINESS_SUCCESS]: (state) => ({
    ...state,
    fetching: false,
    fetched: true,
  }),
  [Types.UNFOLLOW_BUSINESS_FAILURE]: (state, action) => ({
    ...state,
    error: action.error,
    _pendingUnfollowIds: (state._pendingUnfollowIds || []).filter(
      id => id !== (action.error && action.error.business_id)
    ),
  }),
  [Types.GET_FOLLOWED_BUSINESSES_REQUEST]: state => ({
    ...state,
    fetching: true,
    error: null,
  }),
  [Types.GET_FOLLOWED_BUSINESSES_SUCCESS]: (state, action) => {
    const serverResults = (action.data && action.data.results) || [];
    const serverIds = new Set(serverResults.map(b => String(b.business_id)));
    const pendingUnfollowIds = new Set(
      (state._pendingUnfollowIds || []).map(String)
    );
    const pendingOptimistic = getResults(state).filter(
      b => b._optimistic && !serverIds.has(String(b.business_id))
    );
    const filteredServerResults = serverResults.filter(
      b => !pendingUnfollowIds.has(String(b.business_id))
    );
    const remainingPendingUnfollowIds = [...pendingUnfollowIds].filter(
      id => serverIds.has(id)
    );
    return {
      ...state,
      _pendingUnfollowIds: remainingPendingUnfollowIds,
      data: {
        ...(action.data || {}),
        results: [...pendingOptimistic, ...filteredServerResults],
      },
      fetching: false,
      fetched: true,
    };
  },
  [Types.GET_FOLLOWED_BUSINESSES_FAILURE]: getFailureHandler(INITIAL_STATE),
};

export default createReducer(INITIAL_STATE, HANDLERS);
